/**
 * Options for allocating a fee across multiple items
 */
export interface AllocateFeeOptions {
  /** The total fee to allocate */
  totalFee: number;
  /** The number of items to distribute the fee across */
  itemCount: number;
  /** Number of decimal places for rounding (default: 2 for cents) */
  decimalPlaces?: number;
}

/**
 * Result of fee allocation
 */
export interface AllocateFeeResult {
  /** Array of allocated amounts, one per item */
  amounts: number[];
  /** The base amount before remainder distribution */
  baseAmount: number;
  /** Number of items that received an extra unit from the remainder */
  itemsWithRemainder: number;
}

/**
 * Allocates a fee across multiple items using the "largest remainder" method.
 * This ensures no cents are lost due to rounding.
 *
 * Example: $1.00 / 3 = [$0.34, $0.33, $0.33] = $1.00 (no loss)
 *
 * The algorithm:
 * 1. Convert to smallest unit (e.g., cents)
 * 2. Integer division gives base amount
 * 3. Distribute remainder (1 unit each) to first N items
 * 4. Convert back to original unit
 *
 * @param options - The allocation options
 * @returns The allocation result with amounts array and metadata
 */
export function allocateFee(options: AllocateFeeOptions): AllocateFeeResult {
  const { totalFee, itemCount, decimalPlaces = 2 } = options;

  // Handle edge cases
  if (itemCount <= 0) {
    return { amounts: [], baseAmount: 0, itemsWithRemainder: 0 };
  }

  if (totalFee <= 0) {
    return {
      amounts: Array(itemCount).fill(0),
      baseAmount: 0,
      itemsWithRemainder: 0,
    };
  }

  // Convert to smallest unit (e.g., cents for 2 decimal places)
  const multiplier = Math.pow(10, decimalPlaces);
  const totalInSmallestUnit = Math.round(totalFee * multiplier);

  // Integer division for base amount
  const baseInSmallestUnit = Math.floor(totalInSmallestUnit / itemCount);

  // Remainder to distribute (1 unit each to first N items)
  const remainder = totalInSmallestUnit - baseInSmallestUnit * itemCount;

  // Build amounts array
  const amounts: number[] = [];
  for (let i = 0; i < itemCount; i++) {
    const extraUnit = i < remainder ? 1 : 0;
    const amountInSmallestUnit = baseInSmallestUnit + extraUnit;
    amounts.push(amountInSmallestUnit / multiplier);
  }

  return {
    amounts,
    baseAmount: baseInSmallestUnit / multiplier,
    itemsWithRemainder: remainder,
  };
}

/**
 * Gets the allocated fee for a specific index.
 * This is a convenience function for when you only need one value.
 *
 * @param totalFee - The total fee to allocate
 * @param itemCount - The number of items to distribute across
 * @param index - The index of the item (0-based)
 * @param decimalPlaces - Number of decimal places (default: 2)
 * @returns The allocated fee for the specified index
 */
export function getAllocatedFeeForIndex(
  totalFee: number,
  itemCount: number,
  index: number,
  decimalPlaces = 2
): number {
  if (itemCount <= 0 || index < 0 || index >= itemCount) {
    return 0;
  }

  if (totalFee <= 0) {
    return 0;
  }

  const multiplier = Math.pow(10, decimalPlaces);
  const totalInSmallestUnit = Math.round(totalFee * multiplier);
  const baseInSmallestUnit = Math.floor(totalInSmallestUnit / itemCount);
  const remainder = totalInSmallestUnit - baseInSmallestUnit * itemCount;

  const extraUnit = index < remainder ? 1 : 0;
  return (baseInSmallestUnit + extraUnit) / multiplier;
}
